import { useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import NotificationBell from "./NotificationBell";
import OperatorSwitcher from "./OperatorSwitcher";
import {
  LayoutDashboard, Package, PackagePlus, Users, Truck, ClipboardList,
  BarChart3, Settings, LogOut, UserCog, Router, Warehouse, Wrench, Menu, X, Building2
} from "lucide-react";

const roleLabel = { admin: "Admin", gestor: "Gestor", tecnico: "Técnico" };

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Close drawer on route change
  const closeDrawer = () => setDrawerOpen(false);

  if (!user) return null;

  const role = user.role;
  const items = [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: ["admin","gestor","tecnico","almacenero"], tid: "nav-dashboard" },
    { to: "/materiais", label: "Materiais", icon: Package, roles: ["admin","gestor","almacenero"], tid: "nav-materiais" },
    { to: "/equipamentos", label: "Equipamentos", icon: Router, roles: ["admin","gestor","almacenero"], tid: "nav-equipamentos" },
    { to: "/estoque-empresa", label: "Estoque Empresa", icon: Warehouse, roles: ["admin","gestor","almacenero"], tid: "nav-estoque-empresa" },
    { to: "/entradas", label: "Entradas", icon: PackagePlus, roles: ["admin","gestor","almacenero"], tid: "nav-entradas" },
    { to: "/estoque-tecnicos", label: "Estoque Técnicos", icon: Users, roles: ["admin","gestor","almacenero"], tid: "nav-estoque-tecnicos" },
    { to: "/meu-estoque", label: "Meu Estoque", icon: Package, roles: ["tecnico"], tid: "nav-meu-estoque" },
    { to: "/transferencias", label: "Traspasso de Materiais", icon: Truck, roles: ["admin","gestor","tecnico","almacenero"], tid: "nav-transferencias" },
    { to: "/frota", label: "Frota", icon: Truck, roles: ["admin","gestor"], tid: "nav-frota" },
    { to: "/ferramentas", label: "Ferramentas", icon: Wrench, roles: ["admin","gestor"], tid: "nav-ferramentas" },
    { to: "/minhas-ferramentas", label: "Minhas Ferramentas", icon: Wrench, roles: ["tecnico"], tid: "nav-minhas-ferramentas" },
    { to: "/ots", label: "Ordens de Trabalho", icon: ClipboardList, roles: ["admin","gestor","tecnico"], tid: "nav-ots" },
    { to: "/relatorios", label: "Relatórios", icon: BarChart3, roles: ["admin","gestor","tecnico"], tid: "nav-relatorios" },
    { to: "/usuarios", label: "Usuários", icon: UserCog, roles: ["admin"], tid: "nav-usuarios" },
    { to: "/operadoras", label: "Operadoras", icon: Building2, roles: ["admin"], tid: "nav-operadoras" },
    { to: "/config", label: "Configurações", icon: Settings, roles: ["admin","gestor"], tid: "nav-config" },
  ].filter((i) => i.roles.includes(role));

  const currentTitle = items.find((i) => i.to === location.pathname)?.label || "DBS FIELD";

  const SidebarContent = () => (
    <>
      <div className="px-5 py-5 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
        <div className="flex items-center gap-3">
          <img src="/dbs-logo.jpeg" alt="DBS FIELD" className="w-10 h-10 object-cover" style={{ borderRadius: 2 }} />
          <div>
            <div style={{ fontFamily: "Chivo", fontWeight: 700, letterSpacing: "0.05em", fontSize: 15 }}>DBS FIELD</div>
            <div className="text-[10px]" style={{ color: "var(--text-mute)" }}>Gestão de Campo</div>
          </div>
        </div>
        <button className="btn btn-ghost lg:hidden" onClick={closeDrawer} data-testid="close-drawer"><X size={18} /></button>
      </div>

      <nav className="flex-1 py-4 overflow-y-auto">
        {items.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.to === "/"}
            data-testid={it.tid}
            onClick={closeDrawer}
            className={({ isActive }) => `sidebar-item ${isActive ? "active" : ""}`}
          >
            <it.icon size={17} />
            {it.label}
          </NavLink>
        ))}
      </nav>

      <div className="p-4 border-t" style={{ borderColor: "var(--border)" }}>
        <NavLink
          to="/perfil"
          onClick={closeDrawer}
          className="flex items-center gap-3 p-2 mb-3 border"
          style={({ isActive }) => ({
            borderColor: isActive ? "var(--primary)" : "var(--border-strong)",
            background: isActive ? "rgba(56,189,248,0.08)" : "var(--surface-2)",
            textDecoration: "none",
            color: "inherit",
          })}
          data-testid="profile-link"
        >
          <div className="w-9 h-9 flex items-center justify-center" style={{ background: "var(--surface)", border: "1px solid var(--border-strong)" }}>
            <span style={{ fontFamily: "Chivo", fontWeight: 700 }}>{user.name?.[0]?.toUpperCase()}</span>
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm truncate" data-testid="current-user-name">{user.name}</div>
            <span className={`badge badge-role-${role}`} data-testid="current-user-role">{roleLabel[role]}</span>
          </div>
        </NavLink>
        <button
          data-testid="logout-btn"
          className="btn btn-ghost w-full"
          onClick={logout}
        >
          <LogOut size={16} /> Sair
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex" style={{ background: "var(--bg)" }}>
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-[260px] shrink-0 border-r flex-col" style={{ borderColor: "var(--border)", background: "var(--bg)" }}>
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      {drawerOpen && (
        <>
          <div
            className="lg:hidden fixed inset-0 z-40"
            style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }}
            onClick={closeDrawer}
            data-testid="drawer-backdrop"
          />
          <aside
            className="lg:hidden fixed top-0 left-0 bottom-0 w-[280px] z-50 flex flex-col border-r"
            style={{ borderColor: "var(--border)", background: "var(--bg)" }}
            data-testid="mobile-drawer"
          >
            <SidebarContent />
          </aside>
        </>
      )}

      <main className="flex-1 min-w-0 overflow-x-hidden">
        {/* Mobile top bar */}
        <div className="lg:hidden sticky top-0 z-30 flex items-center gap-3 px-4 py-3 border-b" style={{ background: "var(--bg)", borderColor: "var(--border)" }}>
          <button className="btn btn-ghost" onClick={() => setDrawerOpen(true)} data-testid="open-drawer">
            <Menu size={20} />
          </button>
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <img src="/dbs-logo.jpeg" alt="DBS FIELD" className="w-7 h-7 object-cover" style={{ borderRadius: 2 }} />
            <div className="min-w-0">
              <div className="text-xs label-caps truncate">{currentTitle}</div>
              <div className="text-[10px]" style={{ color: "var(--text-mute)" }}>DBS FIELD</div>
            </div>
          </div>
          <OperatorSwitcher testidSuffix="-mobile" />
          <NotificationBell />
        </div>

        {/* Desktop top bar (operator switcher + notifications) */}
        <div className="hidden lg:flex items-center justify-end gap-3 px-6 py-3 border-b" style={{ background: "var(--bg)", borderColor: "var(--border)" }}>
          <OperatorSwitcher />
          <NotificationBell />
        </div>

        <div className="max-w-[1400px] mx-auto p-4 md:p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}
